import { Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { startMicrosoft, useSession } from '../../store/session';
import { newId, useSettings, type CalendarSource, type FeedSource } from '../../store/settings';
import { buttonClass, inputClass, Section, Toggle } from './controls';

const CAL_COLORS = ['#38bdf8', '#f472b6', '#a3e635', '#fbbf24', '#c084fc', '#f87171'];

function validFeedUrl(raw: string): string | null {
  try {
    const u = new URL(raw.trim().replace(/^webcals?:\/\//i, 'https://'));
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.toString() : null;
  } catch {
    return null;
  }
}

function SourceForm({ placeholder, onAdd }: { placeholder: string; onAdd: (name: string, url: string) => void }) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const valid = validFeedUrl(url);
    if (!valid) {
      setError('Enter a valid web address.');
      return;
    }
    onAdd(name.trim() || new URL(valid).hostname, valid);
    setName('');
    setUrl('');
    setError(null);
  };
  return (
    <form onSubmit={submit} className="space-y-2">
      <input className={inputClass} placeholder="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} aria-label="Name" />
      <div className="flex gap-2">
        <input className={inputClass} placeholder={placeholder} value={url} onChange={(e) => setUrl(e.target.value)} aria-label="Address" />
        <button className={buttonClass} type="submit">
          Add
        </button>
      </div>
      {error && <p className="text-sm text-red-400">{error}</p>}
    </form>
  );
}

function SourceRow<T extends FeedSource>({
  source,
  onChange,
  onRemove,
  swatch,
}: {
  source: T;
  onChange: (s: T) => void;
  onRemove: () => void;
  swatch?: string;
}) {
  return (
    <li className="flex items-center gap-3 rounded-lg border border-[var(--border)] px-3 py-2">
      {swatch && <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: swatch }} />}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{source.name}</div>
        <div className="muted truncate text-xs">{source.url.replace(/\?.*$/, '?…')}</div>
      </div>
      <button
        role="switch"
        aria-checked={source.enabled}
        aria-label={`Show ${source.name}`}
        onClick={() => onChange({ ...source, enabled: !source.enabled })}
        className={`h-5 w-9 shrink-0 rounded-full transition ${source.enabled ? 'bg-[var(--accent)]' : 'bg-[var(--border)]'}`}
      >
        <span className={`block h-4 w-4 rounded-full bg-white transition ${source.enabled ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
      </button>
      <button onClick={onRemove} aria-label={`Remove ${source.name}`} className="opacity-60 hover:opacity-100">
        <Trash2 className="h-4 w-4" aria-hidden />
      </button>
    </li>
  );
}

export function CalendarTab() {
  const calendar = useSettings((s) => s.calendar);
  const update = useSettings((s) => s.update);
  const { config, user, microsoft } = useSession();
  const setSources = (sources: CalendarSource[]) => update('calendar', { sources });

  return (
    <>
      <Section title="Calendar overlay">
        <Toggle label="Show calendar" checked={calendar.enabled} onChange={(enabled) => update('calendar', { enabled })} />
        {config?.features.microsoft &&
          (microsoft?.connected ? (
            <Toggle label="Outlook calendar" checked={calendar.outlook} onChange={(outlook) => update('calendar', { outlook })} />
          ) : (
            <div className="flex items-center justify-between gap-4 text-sm">
              <span>Outlook calendar</span>
              {user ? (
                <button className="text-[var(--accent)] hover:underline" onClick={() => startMicrosoft('link')}>
                  Connect Microsoft
                </button>
              ) : (
                <span className="muted text-xs">Sign in to connect</span>
              )}
            </div>
          ))}
        <label className="block text-sm">
          <div className="mb-1 flex justify-between">
            <span>Days ahead</span>
            <span className="muted">{calendar.days}</span>
          </div>
          <input
            type="range"
            min={1}
            max={30}
            value={calendar.days}
            onChange={(e) => update('calendar', { days: Number(e.target.value) })}
            className="w-full accent-[var(--accent)]"
          />
        </label>
      </Section>
      <Section
        title="Calendars"
        description="Paste a private iCal (.ics / webcal://) address. iCloud: Calendar → share → Public Calendar. Google: Settings → calendar → Secret address in iCal format. Outlook: Settings → Shared calendars → Publish. Signing in with Microsoft or Google will connect calendars directly once available."
      >
        <ul className="space-y-2">
          {calendar.sources.map((s) => (
            <SourceRow
              key={s.id}
              source={s}
              swatch={s.color}
              onChange={(n) => setSources(calendar.sources.map((x) => (x.id === s.id ? n : x)))}
              onRemove={() => setSources(calendar.sources.filter((x) => x.id !== s.id))}
            />
          ))}
        </ul>
        <SourceForm
          placeholder="webcal://… or https://….ics"
          onAdd={(name, url) =>
            setSources([
              ...calendar.sources,
              { id: newId(), name, url, enabled: true, color: CAL_COLORS[calendar.sources.length % CAL_COLORS.length]! },
            ])
          }
        />
        <p className="muted text-xs">Calendar addresses are private. They are fetched through the KCL Canvas server and never shown on screen.</p>
      </Section>
    </>
  );
}

export function NewsTab() {
  const news = useSettings((s) => s.news);
  const update = useSettings((s) => s.update);
  const setSources = (sources: FeedSource[]) => update('news', { sources });

  return (
    <>
      <Section title="News ticker">
        <Toggle label="Show news ticker" checked={news.enabled} onChange={(enabled) => update('news', { enabled })} />
        <label className="block text-sm">
          <div className="mb-1 flex justify-between">
            <span>Scroll speed</span>
            <span className="muted">{news.speed < 45 ? 'Slow' : news.speed < 90 ? 'Medium' : 'Fast'}</span>
          </div>
          <input
            type="range"
            min={20}
            max={150}
            value={news.speed}
            onChange={(e) => update('news', { speed: Number(e.target.value) })}
            className="w-full accent-[var(--accent)]"
          />
        </label>
      </Section>
      <Section title="Feeds" description="Any RSS or Atom feed address.">
        <ul className="space-y-2">
          {news.sources.map((s) => (
            <SourceRow
              key={s.id}
              source={s}
              onChange={(n) => setSources(news.sources.map((x) => (x.id === s.id ? n : x)))}
              onRemove={() => setSources(news.sources.filter((x) => x.id !== s.id))}
            />
          ))}
        </ul>
        <SourceForm
          placeholder="https://…/rss.xml"
          onAdd={(name, url) => setSources([...news.sources, { id: newId(), name, url, enabled: true }])}
        />
      </Section>
    </>
  );
}
